import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  CalendarDays,
  Clock,
  KeyRound,
  LogOut,
  Mail,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Card } from "@/platform/ui/card";
import { Input } from "@/platform/ui/input";
import { Label } from "@/platform/ui/label";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import {
  signOut,
  updatePassword,
  updateProfile,
} from "@/platform/auth/api";
import {
  passwordResetSchema,
  profileSettingsSchema,
  type PasswordResetValues,
  type ProfileSettingsValues,
} from "@/platform/auth/schemas";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

export function AccountPage() {
  const { session } = useSession();
  const { profile, refetch } = useProfile();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const checkoutStatus = params.get("checkout");
  const checkoutTenant = params.get("tenant");

  // Pós-checkout Stripe: webhook costuma chegar antes do redirect, mas pode
  // atrasar segundos. Sem polling, useActiveSubscriptions (staleTime 60s)
  // mostra "isActive=false" e o usuário acha que falhou. Aqui invalidamos
  // a cada 2s até a assinatura do tenant aparecer (ou desistir após 30s).
  useEffect(() => {
    if (checkoutStatus !== "success") return;
    if (!checkoutTenant) return;
    const toastId = toast.loading("Confirmando sua assinatura…");
    const startedAt = Date.now();
    const tick = async () => {
      await queryClient.invalidateQueries({ queryKey: ["billing", "active-subscriptions"] });
    };
    const interval = setInterval(() => void tick(), 2_000);
    void tick();
    const stopWhenReady = setInterval(() => {
      const data = queryClient.getQueryData<Array<{ tenant: string }>>([
        "billing",
        "active-subscriptions",
      ]);
      const ready = !!data?.some((s) => s.tenant === checkoutTenant);
      if (ready) {
        clearInterval(interval);
        clearInterval(stopWhenReady);
        toast.success("Assinatura ativada. Bem-vinda!", { id: toastId });
        // Limpa os query params para evitar reentrar no efeito.
        const next = new URLSearchParams(params);
        next.delete("checkout");
        next.delete("tenant");
        setParams(next, { replace: true });
        // Redireciona para o painel do tenant assinado.
        navigate(checkoutTenant === "usp" ? "/usp/painel" : "/painel", {
          replace: true,
        });
      } else if (Date.now() - startedAt > 30_000) {
        clearInterval(interval);
        clearInterval(stopWhenReady);
        toast.error(
          "A confirmação está demorando. Recarregue em alguns segundos ou veja seu e-mail.",
          { id: toastId },
        );
      }
    }, 500);
    return () => {
      clearInterval(interval);
      clearInterval(stopWhenReady);
      toast.dismiss(toastId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, checkoutTenant]);

  if (!session || !profile) {
    return null;
  }

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="default" className="relative py-16 md:py-20">
        <header className="animate-fade-up">
          <Badge variant="accentSolid">
            <Sparkles className="size-3" aria-hidden />
            Conta
          </Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Configurações da <span className="text-gradient-accent">conta.</span>
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Atualize seus dados, senha ou encerre a sessão.
          </p>
        </header>

        {/* User card */}
        <Card className="mt-10 p-7 md:p-8">
          <div className="flex items-center gap-4">
            <span className="chip size-14 bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)]">
              <UserIcon className="size-6" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-[length:var(--text-h3)] font-semibold">
                {profile.display_name ?? profile.full_name ?? "Estudante"}
              </p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                <Mail className="size-3.5" aria-hidden />
                {session.user.email}
              </p>
            </div>
            {profile.is_admin ? <Badge variant="accent">admin</Badge> : null}
          </div>
        </Card>

        <ProfileForm
          userId={session.user.id}
          initial={{
            display_name: profile.display_name ?? "",
            exam_date: profile.exam_date ?? "2026-11-22",
            weekly_hours: profile.weekly_hours ?? 12,
            consent_research: profile.consent_research,
          }}
          onSaved={refetch}
        />

        <PasswordForm />

        <Card className="mt-6 p-7 md:p-8 border-2 border-[color:var(--color-warning)]/30">
          <div className="flex items-start gap-4">
            <span className="chip size-12 bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)] shrink-0">
              <AlertTriangle className="size-5" aria-hidden />
            </span>
            <div className="flex-1">
              <Badge variant="warning">Zona de atenção</Badge>
              <h2 className="mt-3 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)]">
                Encerrar sessão
              </h2>
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Você sai de todos os dispositivos. Pra excluir a conta definitivamente, escreva
                para <a href="mailto:privacidade@psieinstein.com.br" className="link font-medium">privacidade@psieinstein.com.br</a>.
              </p>
              <div className="mt-5">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={async () => {
                    try {
                      await signOut();
                      toast.success("Sessão encerrada.");
                      navigate("/", { replace: true });
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : "Falha ao sair.");
                    }
                  }}
                >
                  <LogOut className="size-4" aria-hidden />
                  Sair desta sessão
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
}

function ProfileForm({
  userId,
  initial,
  onSaved,
}: {
  userId: string;
  initial: ProfileSettingsValues;
  onSaved: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<ProfileSettingsValues>({
    resolver: zodResolver(profileSettingsSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.display_name, initial.exam_date, initial.weekly_hours, initial.consent_research]);

  async function onSubmit(values: ProfileSettingsValues) {
    setSubmitting(true);
    try {
      await updateProfile(userId, values);
      await onSaved();
      toast.success("Dados atualizados.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mt-6 p-7 md:p-8">
      <div className="flex items-center gap-3">
        <Badge variant="accent">Perfil</Badge>
      </div>
      <h2 className="mt-3 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)]">
        Seus dados
      </h2>
      <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
        O plano semanal se recalibra automaticamente quando você mexe nas horas/semana ou na data.
      </p>

      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-6" noValidate>
        <FieldRow
          label="Como devo te chamar"
          icon={UserIcon}
          error={form.formState.errors.display_name?.message}
          input={
            <Input type="text" autoComplete="name" {...form.register("display_name")} />
          }
        />
        <FieldRow
          label="Data prevista da prova"
          icon={CalendarDays}
          error={form.formState.errors.exam_date?.message}
          input={<Input type="date" {...form.register("exam_date")} />}
        />
        <FieldRow
          label="Horas de estudo por semana"
          icon={Clock}
          error={form.formState.errors.weekly_hours?.message}
          input={
            <Input
              type="number"
              min={1}
              max={60}
              {...form.register("weekly_hours", { valueAsNumber: true })}
            />
          }
        />

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-4">
          <input
            type="checkbox"
            {...form.register("consent_research")}
            className="mt-0.5 size-5 rounded-sm border-2 border-[--color-border] accent-[--color-accent] cursor-pointer"
          />
          <span className="space-y-1">
            <span className="block text-[length:var(--text-body-sm)] font-medium">
              Contribuir com pesquisa anônima
            </span>
            <span className="block text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
              Autorizo uso anônimo das minhas redações e tentativas pra melhorar o produto.
              Revogável a qualquer momento.
            </span>
          </span>
        </label>

        <Button type="submit" size="lg" loading={submitting} disabled={!form.formState.isDirty}>
          Salvar alterações
          <ArrowUpRight className="size-4" aria-hidden />
        </Button>
      </form>
    </Card>
  );
}

function PasswordForm() {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<PasswordResetValues>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: { password: "", confirm: "" },
  });

  async function onSubmit(values: PasswordResetValues) {
    setSubmitting(true);
    try {
      await updatePassword(values.password);
      toast.success("Senha atualizada.");
      form.reset({ password: "", confirm: "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao atualizar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mt-6 p-7 md:p-8">
      <Badge variant="mint">Segurança</Badge>
      <h2 className="mt-3 font-display text-[length:var(--text-h3)] font-semibold leading-[var(--text-h3--line-height)] tracking-[var(--text-h3--letter-spacing)]">
        Alterar senha
      </h2>
      <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
        Mínimo 8 caracteres. Você continua logada após trocar.
      </p>

      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-6" noValidate>
        <FieldRow
          label="Nova senha"
          icon={KeyRound}
          error={form.formState.errors.password?.message}
          input={
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              {...form.register("password")}
            />
          }
        />
        <FieldRow
          label="Confirmar"
          icon={KeyRound}
          error={form.formState.errors.confirm?.message}
          input={
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="Repita"
              {...form.register("confirm")}
            />
          }
        />

        <Button type="submit" size="lg" loading={submitting}>
          Atualizar senha
        </Button>
      </form>
    </Card>
  );
}

function FieldRow({
  label,
  icon: Icon,
  input,
  error,
}: {
  label: string;
  icon: typeof UserIcon;
  input: React.ReactNode;
  error?: string;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-start sm:gap-5">
      <span className="chip size-10 shrink-0 bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent)]">
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="space-y-1.5">
        <Label>{label}</Label>
        {input}
        {error ? (
          <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
            <AlertCircle className="size-3.5" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
